// Tag chip editor — comma/Enter to add, × to remove. Used by the task create
// form (local set, persisted on create), the task drawer, and the ticket detail
// (persisted via the tag set routes). Styling reuses the .tags-* tokens.

import { useState, type KeyboardEvent } from "react";

/** Normalize/dedupe a tag name (trim, collapse, lower-case for comparison). */
function clean(name: string): string {
  return name.trim().replace(/\s+/g, " ");
}

/** Read-only chip row (e.g. the board card, or a non-editing detail view). */
export function TagChips({ tags }: { tags: string[] }) {
  if (tags.length === 0) return null;
  return (
    <span className="tags-row">
      {tags.map((t) => (
        <span key={t} className="tag-chip">
          {t}
        </span>
      ))}
    </span>
  );
}

/**
 * Editable tag input. The parent owns the `value` array; `onChange` fires with
 * the next set on every add/remove. `disabled` blocks edits while a save runs.
 */
export function TagsEditor({
  value,
  onChange,
  disabled = false,
  placeholder = "Add a tag…",
  inputId,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  inputId?: string;
}) {
  const [draft, setDraft] = useState("");

  function add(raw: string) {
    const name = clean(raw);
    if (!name) return;
    // Case-insensitive dedupe; keep the existing casing if already present.
    if (value.some((t) => t.toLowerCase() === name.toLowerCase())) {
      setDraft("");
      return;
    }
    onChange([...value, name]);
    setDraft("");
  }

  function remove(tag: string) {
    onChange(value.filter((t) => t !== tag));
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      add(draft);
    } else if (e.key === "Backspace" && draft === "" && value.length > 0) {
      // Quick-remove the last chip when the field is empty.
      remove(value[value.length - 1]!);
    }
  }

  return (
    <div className={`tags-editor${disabled ? " is-disabled" : ""}`}>
      {value.map((t) => (
        <span key={t} className="tag-chip">
          {t}
          <button
            type="button"
            className="tag-x"
            aria-label={`Remove tag ${t}`}
            disabled={disabled}
            onClick={() => remove(t)}
          >
            ×
          </button>
        </span>
      ))}
      <input
        id={inputId}
        type="text"
        className="tags-input"
        value={draft}
        disabled={disabled}
        placeholder={value.length === 0 ? placeholder : ""}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => add(draft)}
      />
    </div>
  );
}
